import { createAddress, deleteAddress, updateAddress, updateDefaultAddress } from "@app/lib/customer";

let address_components: any;

class AccountAddresses extends HTMLElement {
  $successMessage: HTMLElement;

  constructor() {
    super();
    this.$successMessage = this.querySelector(".form-success-message")!;
  }

  connectedCallback() {
    // Display the relevant page
    const edit = new URLSearchParams(window.location.search).get("edit");
    const allElements = this.querySelectorAll(
      ".element--address-list, .element--new-address, .element--existing-address",
    );
    allElements.forEach((element) => {
      element.classList.add("!hidden");
    });

    switch (edit) {
      case "new":
        this.querySelectorAll(".element--new-address")?.forEach((element) => {
          element.classList.remove("!hidden");
        });
        this.setupNewAddressForm();
        break;

      case "existing":
        this.querySelectorAll(".existing-address-form")?.forEach((element) => {
          element.classList.remove("!hidden");
        });
        this.setupExistingAddressForm();
        this.setupDeleteAddressButton();
        break;

      default:
        this.querySelectorAll(".element--address-list")?.forEach((element) => {
          element.classList.remove("!hidden");
        });
    }

    // Display success message
    if (window.location.search.includes("added=true")) {
      this.showSuccessMessage("added");
    }
    if (window.location.search.includes("edited=true")) {
      this.showSuccessMessage("edited");
    }
    if (window.location.search.includes("deleted=true")) {
      this.showSuccessMessage("deleted");
    }
  }

  showSuccessMessage(type: string) {
    switch (type) {
      case "added":
        this.$successMessage.innerText = "Your new address has been saved.";
        this.$successMessage.classList.remove("hidden");
        break;

      case "edited":
        this.$successMessage.innerText = "Your address has been saved.";
        this.$successMessage.classList.remove("hidden");
        break;

      case "deleted":
        this.$successMessage.innerText = "Your address has been removed.";
        this.$successMessage.classList.remove("hidden");
        break;

      default:
        this.$successMessage.classList.add("hidden");
        break;
    }
  }

  setupNewAddressForm() {
    const $form = document.querySelector(".new-address-form") as HTMLFormElement;
    const submit = async (e: any) => {
      if (!address_components) {
        const input = $form.querySelector('[name="address"]') as HTMLInputElement | null;
        const inputWrapper = input?.closest(".snippet-input") as HTMLDivElement | null;
        if (inputWrapper) window.inputError(inputWrapper, "Please select an address");
        return;
      }

      const data = new FormData($form) as any;
      const $submitButton = $form.querySelector("button[type='submit']") as HTMLButtonElement;

      window.inputErrorClearAll($form);
      $submitButton.disabled = true;

      const { customerAddress, customerUserErrors } = await createAddress({
        firstName: data.get("firstName") || "",
        lastName: data.get("lastName") || "",
        company: data.get("company") || undefined,
        address1: address_components.address1 || "",
        city: address_components.city || "",
        province: address_components.province || "",
        country: address_components.country || "",
        zip: address_components.zip || "",
      });

      $submitButton.disabled = false;

      if (customerUserErrors.length > 0) {
        customerUserErrors.forEach((error: any) => {
          const input = $form.querySelector(
            `[name="${error.field[error.field.length - 1]}"]`,
          ) as HTMLInputElement | null;
          const inputWrapper = input?.closest(".snippet-input") as HTMLDivElement | null;
          if (inputWrapper) window.inputError(inputWrapper, error.message);
        });
      } else {
        if (data.get("default_address") === "on") {
          await updateDefaultAddress(customerAddress.id);
        }
        window.location.href = (this.dataset.url || "/account/addresses") + "?added=true";
      }
    };
    $form?.addEventListener("submit", async (e: any) => {
      e.preventDefault();
      // hack to allow google autocomplete to do its thing before submitting
      setTimeout(() => submit(e), 100);
    });
  }

  setupExistingAddressForm() {
    const $form = document.querySelector(".existing-address-form") as HTMLFormElement;
    const addressId = new URLSearchParams(window.location.search).get("id");
    const address = window.customer?.addresses.find((a: any) => a.id == addressId);

    if (address) {
      const $firstName = $form.querySelector("input[name='firstName']") as HTMLInputElement;
      const $lastName = $form.querySelector("input[name='lastName']") as HTMLInputElement;
      const $company = $form.querySelector("input[name='company']") as HTMLInputElement;
      const $address = $form.querySelector("input[name='address']") as HTMLInputElement;
      const $defaultAddress = $form.querySelector("input[name='default_address']") as HTMLInputElement;

      $firstName.value = address.first_name;
      $lastName.value = address.last_name;
      $company.value = address.company;
      $address.value = [
        address.address1,
        [address.city, address.province_code, address.zip].filter(Boolean).join(" ")
      ].filter(Boolean).join(", ");
      if (window.customer?.defaultAddressId == address.id) $defaultAddress.checked = true;
    }

    const submit = async (e: any) => {
      const data = new FormData($form) as any;
      const $submitButton = $form.querySelector("button[type='submit']") as HTMLButtonElement;

      window.inputErrorClearAll($form);
      $submitButton.disabled = true;

      const { customerAddress, customerUserErrors } = await updateAddress(
        `gid://shopify/MailingAddress/${address.id}?model_name=MailingAddress`,
        {
          firstName: data.get("firstName") || "",
          lastName: data.get("lastName") || "",
          company: data.get("company") || "",
          address1: address_components?.address1 || undefined,
          city: address_components?.city || undefined,
          province: address_components?.province || undefined,
          country: address_components?.country || undefined,
          zip: address_components?.zip || undefined,
        },
      );

      $submitButton.disabled = false;

      if (customerUserErrors.length > 0) {
        customerUserErrors.forEach((error: any) => {
          const input = $form.querySelector(
            `[name="${error.field[error.field.length - 1]}"]`,
          ) as HTMLInputElement | null;
          const inputWrapper = input?.closest(".snippet-input") as HTMLDivElement | null;
          if (inputWrapper) window.inputError(inputWrapper, error.message);
        });
      } else {
        if (data.get("default_address") === "on" && customerAddress.id != window.customer?.defaultAddressId) {
          await updateDefaultAddress(customerAddress.id);
        }
        window.location.href = (this.dataset.url || "/account/addresses") + "?edited=true";
      }
    };

    $form?.addEventListener("submit", async (e: any) => {
      e.preventDefault();
      // hack to allow google autocomplete to do its thing before submitting
      setTimeout(() => submit(e), 100);
    });
  }

  setupDeleteAddressButton() {
    const $button = document.querySelector(".delete-address-btn") as HTMLButtonElement;
    $button.addEventListener("click", async () => {
      $button.disabled = true;
      let id = new URLSearchParams(window.location.search).get("id")!;
      id = `gid://shopify/MailingAddress/${id}?model_name=MailingAddress`;
      const { customerUserErrors } = await deleteAddress(id);
      if (customerUserErrors.length > 0) {
        console.log("customerUserErrors", customerUserErrors);
      } else {
        window.location.href = (this.dataset.url || "/account/addresses") + "?deleted=true";
      }
    });
  }
}

window.customElements.define("account-addresses", AccountAddresses);

// Called by the google js script
window.initPlacesApi = () => {
  if (!window.google) return;

  const inputs = document.querySelectorAll(".address-field input");
  const options = {
    componentRestrictions: { country: "au" },
    fields: ["address_components"],
    types: ["address"],
  };

  inputs.forEach((input) => {
    const autocomplete = new window.google.maps.places.Autocomplete(input, options);
    autocomplete.addListener("place_changed", () => {
      const { address_components: ac } = autocomplete.getPlace();
      address_components = {
        address1:
          (ac.find((p: any) => p.types.includes("street_number"))?.long_name || "") +
          " " +
          (ac.find((p: any) => p.types.includes("route"))?.long_name || ""),
        city: ac.find((p: any) => p.types.includes("locality"))?.long_name || "",
        province: ac.find((p: any) => p.types.includes("administrative_area_level_1"))?.long_name || "",
        country: ac.find((p: any) => p.types.includes("country"))?.long_name || "",
        zip: ac.find((p: any) => p.types.includes("postal_code"))?.long_name || "",
      };
    });
  });
};
